import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Stars from '@/components/general/Stars.jsx';
import isracardLogoOutline from '@/img/isracard/isracard-logo-outline.png';
import infoIcon from '@/img/isracard/info-icon.svg';
import { ReactComponent as XCloseIcon } from '@/img/bookings/x-close.svg';
import { Button, CircularProgress } from '@mui/material';
import moment from 'moment/moment';
import DealActions from '@/actions/DealActions';
import DealStore from '@/stores/DealStore';
import DealConstants from '@/constants/DealConstants';
import ResultsStore from '@/stores/ResultsStore';
import { email_regex } from 'arbitrip-common/general/constants/regexes';
import Analytics from 'arbitrip-common/client/analytics';
import ProfileStore from '@/stores/ProfileStore';
import { addUtmContentToUrl } from 'arbitrip-common/client/utils/UtmContent';

const ISRACARD_LOBBY_URL = 'https://i.isracard.co.il/mO86/p9k7ag5i';

const IsracardPopup = ({ isView, onClose, hotelName, price, nights, stars, reviewCount, city, country }) => {
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [bookingRequestStatus, setBookingRequestStatus] = useState(DealConstants.DEAL_STATUS.INITIAL);
    const { t } = useTranslation();

    useEffect(() => {
        const handleStoreChange = () => {
            setBookingRequestStatus(DealStore.getBookingRequestStatus());
        };
        DealStore.addChangeListener(handleStoreChange);
        return () => DealStore.removeChangeListener(handleStoreChange);
    }, []);

    useEffect(() => {
        if (isView) {
            DealActions.resetBookingRequest();
        }
    }, [isView]);

    useEffect(() => {
        if (isView) {
            Analytics.actions.views.openIsracardCardIssuancePopup(ProfileStore.getProfile(), hotelName, price);
        }
    }, [isView, hotelName, price]);

    if (!isView) return null;

    const isSubmitting = bookingRequestStatus === DealConstants.DEAL_STATUS.BUSY;
    const isSuccess = bookingRequestStatus === DealConstants.DEAL_STATUS.SUCCESS;
    const isError = bookingRequestStatus === DealConstants.DEAL_STATUS.FAIL;

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setEmailError(false);
    };

    const handleSubmit = () => {
        if (isSubmitting) return;

        if (!email_regex.test(email)) {
            setEmailError(true);
            return;
        }

        setEmailError(false);

        const searchTerms = ResultsStore.getCurrentSearchSession()?.search_terms || {};
        const checkIn = searchTerms.check_in ? moment(searchTerms.check_in).format('YYYY-MM-DD') : '';
        const checkOut = searchTerms.check_out ? moment(searchTerms.check_out).format('YYYY-MM-DD') : '';
        const guests = searchTerms.guests || 0;
        const rooms = searchTerms.rooms || 0;
        const childrenAges = searchTerms.children_ages || [];

        Analytics.actions.views.sentIsracardCardIssuanceEmail(ProfileStore.getProfile(), hotelName, price);

        DealActions.isracardAnonymousBookingRequest({
            email,
            hotelName,
            city,
            country,
            checkIn,
            checkOut,
            guests,
            rooms,
            childrenAges,
            price,
        });
    };

    return (
        <div className="isracard-popup" data-testid="isracard-popup">
            <button
                className="isracard-popup__close-btn"
                data-testid="isracard-popup-close-btn"
                onClick={onClose}
                aria-label={t('IsracardPopup.CloseAriaLabel')}
            >
                <XCloseIcon />
            </button>

            <div className="isracard-popup__right">
                <div className="isracard-popup__right-block-1">
                    <div className="isracard-popup__right-block-1-inner">
                        <div className="isracard-popup__headline-block">
                            <p className="isracard-popup__headline">{t('IsracardPopup.Headline')}</p>
                            <div className="isracard-popup__subtext">
                                <p>{t('IsracardPopup.SubtextPart1')}</p>
                                <p>{t('IsracardPopup.SubtextPart2')}</p>
                                <p>
                                    <Trans
                                        i18nKey="IsracardPopup.SubtextPart3"
                                        components={{ br: <br /> }}
                                    />
                                    </p>
                            </div>
                        </div>
                        <Button className="isracard-popup__cta-btn" variant="outlined" disableRipple onClick={() => window.open(addUtmContentToUrl(ISRACARD_LOBBY_URL), '_blank')}>
                            {t('IsracardPopup.CtaButton')}
                        </Button>
                    </div>
                    <p className="isracard-popup__disclaimer" dir="auto">
                        {t('IsracardPopup.Disclaimer')}
                    </p>
                </div>
                <div className="isracard-popup__right-block-2">
                    <img
                        className="isracard-popup__logo-outline"
                        src={isracardLogoOutline}
                        alt="Isracard Logo"
                        aria-hidden="true"
                    />
                </div>
            </div>

            <div className="isracard-popup__left">
                <div className="isracard-popup__hotel-row">
                    <div className="isracard-popup__hotel-meta">
                        <p className="isracard-popup__hotel-name">{hotelName}</p>
                        <div className="isracard-popup__rating">
                            <Stars stars={stars} />
                            <span className="isracard-popup__review-count">
                                {t('IsracardPopup.Reviews', { count: reviewCount })}
                            </span>
                        </div>
                    </div>

                    <div className="isracard-popup__price-section">
                        <span className="isracard-popup__price-from">{t('IsracardPopup.PriceFrom')}</span>
                        <span className="isracard-popup__price">{price}</span>
                        <span className="isracard-popup__price-nights">
                            {t('IsracardPopup.PriceNights')}
                            <strong>{nights === 1 ? t('IsracardPopup.OneNight') : t('IsracardPopup.Nights', { count: nights })}</strong>
                        </span>
                        <div className="isracard-popup__tax-row">
                            <span className="isracard-popup__tax-label">{t('IsracardPopup.TaxIncluded')}</span>
                            <img className="isracard-popup__info-icon" src={infoIcon} alt="" aria-hidden="true" />
                        </div>
                    </div>
                </div>

                <div className="isracard-popup__divider" />

                <div className="isracard-popup__form-section">
                    <div className="isracard-popup__marketing-text-title">{t('IsracardPopup.MarketingTextTitle')}</div>
                    <p className="isracard-popup__marketing-text">{t('IsracardPopup.MarketingText')}</p>
                    {isSuccess ? (
                        <p className="isracard-popup__success-label" data-testid="isracard-popup-success-label">{t('IsracardPopup.SuccessLabel')}</p>
                    ) : (
                        <div>
                            <div className="isracard-popup__email-row">
                                <input
                                    className={`isracard-popup__email-input${emailError ? ' isracard-popup__email-input--error' : ''}`}
                                    type="email"
                                    data-testid="isracard-popup-email-input"
                                    placeholder={t('IsracardPopup.EmailPlaceholder')}
                                    value={email}
                                    onChange={handleEmailChange}
                                    dir="auto"
                                />
                                <button
                                    className={`isracard-popup__send-btn${isSubmitting ? ' isracard-popup__send-btn--loading' : ''}`}
                                    data-testid="isracard-popup-send-btn"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        t('IsracardPopup.SendButton')
                                    )}
                                </button>
                            </div>
                            {emailError && (
                                <p className="isracard-popup__email-error">{t('IsracardPopup.EmailValidationError')}</p>
                            )}
                            {isError && <p className="isracard-popup__submit-error">{t('IsracardPopup.ErrorLabel')}</p>}
                        </div>
                    )}
                </div>
                <div className="isracard-popup__legal-notes">
                    <p className="isracard-popup__legal-note-item">
                        <Trans i18nKey="IsracardPopup.LegalNote1" components={{ br: <br /> }} />
                    </p>

                    <p className="isracard-popup__legal-note-item">
                        <Trans
                            i18nKey="IsracardPopup.LegalNote2"
                            components={{
                                br: <br />,
                                privacyLink: (
                                <a
                                className="isracard-popup__privacy-link"
                                    href="https://isracard-assets.s3.eu-central-1.amazonaws.com/time_off_privacy_policy_hotels.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                                )
                            }}
                            />
                    </p>
                </div>
            </div>
        </div>
    );
};

export default IsracardPopup;
